import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Layers, LogIn } from 'lucide-react';
import { FeedItem } from './FeedPage';
import LoginModal from '../components/LoginModal';
import apiService from '../services/apiService';
import { useToast } from '../components/Toast'; // Import Toast

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const aurora = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// --- Styled Components ---
const PageContainer = styled.div`
  min-height: 100vh;
  background: #000;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const NavBar = styled.nav`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 24px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 100;

  @media (max-width: 768px) {
    padding: 20px 24px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 800;
  font-size: 1.5rem;
  letter-spacing: -0.5px;
  color: #fff;
`;

const NavBtn = styled.button`
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  padding: 10px 20px;
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  white-space: nowrap; /* Prevent line break */

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const HeroSection = styled.section`
  min-height: 100vh; /* 80vh -> 100vh로 꽉 차게 변경 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 0 20px; /* 중앙 정렬을 위해 상단 패딩 제거하고 flex로 제어 */
  padding-top: 80px; /* 기본 네비바 공간 확보 */
  position: relative;
  overflow: hidden;

  /* AI Background Image */
  background-image: 
    linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.9)), 
    url('/images/ai_learning_bg.jpg');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  /* 모바일 대응 */
  @media (max-width: 768px) {
    min-height: 90vh;
    padding-top: 100px; /* 모바일에서 더 넉넉하게 */
    justify-content: flex-start; /* 위로 좀 더 붙도록 */
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, transparent 0%, #000 100%);
    opacity: 0.8;
    z-index: 0;
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 800px;
  animation: ${fadeIn} 1s ease-out;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.3;
  margin-bottom: 24px;
  background: linear-gradient(to right, #fff, #a5a5a5);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  word-break: keep-all;

  @media (max-width: 768px) {
    font-size: 2rem; /* 2.2 -> 2.0rem 조금 더 축소 */
    line-height: 1.3;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #c0c0c0;
  margin-bottom: 40px;
  line-height: 1.6;
  word-break: keep-all;
  max-width: 600px;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 1rem; /* 1.1 -> 1.0rem */
    padding: 0 20px;
    margin-bottom: 32px;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #e50914 0%, #ff4f58 100%);
  color: #fff;
  border: none;
  font-size: 1.25rem;
  font-weight: 700;
  padding: 20px 52px;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(229, 9, 20, 0.4);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.5px;
  white-space: nowrap; /* 텍스트 줄바꿈 방지 */

  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 20px 50px rgba(229, 9, 20, 0.5);
    filter: brightness(1.1);
  }
  
  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    padding: 16px 32px; /* 모바일 패딩 축소 */
    width: auto;
    min-width: 200px;
    justify-content: center;
  }
`;

const DemoSection = styled.section`
  padding: 80px 20px;
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SectionHeader = styled.h2`
  font-size: 2rem;
  margin-bottom: 12px;
  color: #fff;
`;

const SectionDesc = styled.p`
  color: #666;
  margin-bottom: 48px;
  font-size: 1.1rem;
`;

const FeedWrapper = styled.div`
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

// Static Fallback for 0ms First Paint
const STATIC_DEMO_ITEMS = [
  {
    id: 'static-1',
    text: "Artificial Intelligence is processing this content in real-time. Experience the speed of Nureum Voca's analysis engine.",
    translation: "인공지능이 이 콘텐츠를 실시간으로 처리하고 있습니다. 누름보카 분석 엔진의 속도를 경험해보세요.",
    topic: "Tech",
    sourceCode: "System",
    createdAt: new Date().toISOString(),
    likes: 120,
    liked: false,
    imageUrl: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: 'static-2',
    text: "Language learning is about consistency. Small daily habits lead to massive fluency over time.",
    translation: "언어 학습은 꾸준함에 관한 것입니다. 매일의 작은 습관들이 시간이 지나면 엄청난 유창함으로 이어집니다.",
    topic: "Education",
    sourceCode: "System",
    createdAt: new Date().toISOString(),
    likes: 85,
    liked: false,
    imageUrl: "https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&q=80&w=800"
  },
  {
    id: 'static-3',
    text: "Global markets react to new technologies instantly. Stay ahead by reading the latest news in English.",
    translation: "글로벌 시장은 새로운 기술에 즉각 반응합니다. 영어로 최신 뉴스를 읽으며 앞서 나가세요.",
    topic: "Economy",
    sourceCode: "System",
    createdAt: new Date().toISOString(),
    likes: 92,
    liked: false,
    imageUrl: "https://images.unsplash.com/photo-1611974765270-ca12586343bb?auto=format&fit=crop&q=80&w=800"
  }
];

function LandingPage({ onLoginSuccess }) {
  const navigate = useNavigate();
  const demoRef = useRef(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { showToast } = useToast(); // Hook

  // Initialize with STATIC items for INSTANT render
  const [demoItems, setDemoItems] = useState(STATIC_DEMO_ITEMS);

  const [revealedIds, setRevealedIds] = useState(new Set());

  // Fetch Real Feed Items for Demo (Background Refresh)
  React.useEffect(() => {
    const fetchDemoItems = async () => {
      // 1. Instant Cache Load
      const cached = localStorage.getItem('demo_feed_cache');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.length > 0) {
            setDemoItems(parsed);
          }
        } catch (e) { console.error("Cache Error", e); }
      }

      try {
        // 2. Fetch Fresh Data (Limit 5 for speed)
        const response = await apiService.getFeed({ type: 'init', contentType: 'news', limit: 5 });
        let items = response.data || [];

        // Filter items with content
        items = items.filter(i => i.text && i.text.length > 50);

        // Prioritize items with audio/images
        const withImages = items.filter(i => i.imageUrl);
        const withoutImages = items.filter(i => !i.imageUrl);

        const merged = [...withImages, ...withoutImages].slice(0, 3);

        if (merged.length > 0) {
          setDemoItems(merged); // Silent Update
          localStorage.setItem('demo_feed_cache', JSON.stringify(merged));
        }
      } catch (e) {
        console.error("Failed to load demo feed, keeping static/cache", e);
      }
    };

    fetchDemoItems();
  }, []);

  const toggleReveal = (id) => {
    setRevealedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const scrollToDemo = () => {
    demoRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGuestDemo = () => {
    const guestUser = {
      id: 'temp00000',
      displayName: 'Visiter',
      email: 'guest@temp.com',
      isGuest: true
    };
    localStorage.setItem("guestLastLogin", new Date().toISOString());

    localStorage.setItem("user", JSON.stringify(guestUser));
    if (onLoginSuccess) onLoginSuccess(guestUser);
  };

  const handleLoginRequest = () => {
    setShowLoginModal(true);
  };

  return (
    <PageContainer>
      {showLoginModal && (
        <LoginModal
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={(user) => {
            setShowLoginModal(false);
            if (onLoginSuccess) onLoginSuccess(user);
          }}
        />
      )}

      <NavBar>
        <Logo>
          <Layers color="#e50914" size={32} strokeWidth={2.5} />
          NUREUM VOCA
        </Logo>
        <NavBtn onClick={handleLoginRequest}>
          <LogIn size={18} />
          로그인
        </NavBtn>
      </NavBar>

      <HeroSection>
        <HeroContent>
          <Title>억지로 외우지 마세요.<br />그저 누르기만 하세요.</Title>
          <Subtitle>
            영어가 일상이 되는 가장 자연스러운 방법.<br />
            관심 있는 뉴스를 읽고, 단어를 누르면 AI가 문맥까지 즉시 분석합니다.
          </Subtitle>
          <CTAButton onClick={handleGuestDemo}>
            인터랙티브 데모 체험하기
            <ArrowRight size={20} />
          </CTAButton>
        </HeroContent>
      </HeroSection>

      <DemoSection ref={demoRef}>
        <SectionHeader>살아있는 영어 경험</SectionHeader>
        <SectionDesc>직접 단어를 눌러보세요. 마법 같은 분석이 시작됩니다.</SectionDesc>

        <FeedWrapper>
          {demoItems.map(item => (
            <FeedItem
              key={item.id}
              item={item}
              revealedIds={revealedIds}
              toggleReveal={toggleReveal}
              // Dummy Handlers
              handleLike={() => { }}
              handleShare={() => { }}
              setAnalysisText={() => { }}
              setAnalysisFeedId={() => { }}
              setShowAnalysis={() => { }}
              setDeepDiveItem={() => { }}
              getSourceDisplay={(i) => i.sourceCode}
              getTimeDisplay={() => "Live Demo"}
              handleSmartWordClick={() => {
                alert("상세분석은 로그인을 하셔야 합니다");
                handleLoginRequest();
              }}
              onMeaningClick={() => {
                alert("상세분석은 로그인을 하셔야 합니다");
                handleLoginRequest();
              }}

              // CRITICAL: GUEST MODE
              isGuest={true}
              onRequestLogin={handleLoginRequest}
            />
          ))}
        </FeedWrapper>

        <div style={{ marginTop: 60, textAlign: 'center' }}>
          <p style={{ color: '#888', marginBottom: 20 }}>더 많은 콘텐츠를 원하시나요?</p>
          <CTAButton onClick={handleLoginRequest} style={{ fontSize: '1.1rem', padding: '16px 40px' }}>
            무료로 시작하기
          </CTAButton>
        </div>

      </DemoSection>
    </PageContainer>
  );
}

export default LandingPage;
